'use client'

import { Dispatch, createContext, useContext, useEffect, useReducer} from "react"
import { useSession } from 'next-auth/react'

export type ValidUser = {
  id: string, 
  firstName: string, 
  lastName: string, 
  email: string, 
  company: string, 
  profession: string
}

export type UserDispatchActions = (
  {type: 'update', user: ValidUser } | 
  {type: 'refresh' } 
)

export type UserContextState = {
  user: ValidUser 
}

export type UserContextHook = {
  state: UserContextState, 
  dispatch: Dispatch<UserDispatchActions>, 
  status: 'authenticated' | 'loading' | 'unauthenticated'
}

const initialState:UserContextState = {
  user: {
    id: '0', 
    firstName: 'John', 
    lastName: 'Smith', 
    email: 'john.smith@gmail.com', 
    company: 'Colton Lane', 
    profession: 'Manager'
  }
}

const userReducer = (current: UserContextState, action: UserDispatchActions)=>{
  const copy = {...current}
  switch(action.type){
    case 'update': 
      copy.user = action.user
    break; 
  }
  return copy
}

const UserContext = createContext<UserContextHook | null>(null)


export function UserContextProvider({children}:{children: React.ReactNode}){
  const [state, dispatch] = useReducer(userReducer, initialState)
  const {data: session, status} = useSession()


  useEffect(()=>{
    dispatch({type: 'update', user: {
      id: session?.user.id ??  '0',
      firstName: session?.user.firstName  ??'John',
      lastName: session?.user.lastName ?? 'Smith',
      email: session?.user.email ?? 'your@email.com',
      profession: session?.user.profession ?? 'Your Profession',
      company: session?.user.company ?? 'Colton Lane'
    }})
    // Depend on the session fields themselves, not just status: a profile
    // save calls session.update() without changing status, and the account
    // form must re-derive from the refreshed values on remount/client-nav.
  }, [
    status,
    session?.user.id,
    session?.user.firstName,
    session?.user.lastName,
    session?.user.email,
    session?.user.profession,
    session?.user.company,
  ])

  return (
    <UserContext.Provider value ={{state, dispatch, status}}>
      {children}
    </UserContext.Provider>
  )
}

export function useUserContext(){
  const context = useContext(UserContext)
  if (context === null){
    throw new Error('Missing user context')
  }
  return context
}

